import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'

function BlogDetail() {
  const { slug } = useParams()
  const [post, setPost] = useState(null)
  const [relatedPosts, setRelatedPosts] = useState([])
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'not-found' | 'error'

  useEffect(() => {
    let cancelled = false
    setStatus('loading')

    axios
      .get('/api/blogs/', { params: { status: 'published' } })
      .then((response) => {
        if (cancelled) return
        const blogs = response.data?.blogs ?? []
        const found = blogs.find((item) => item.slug === slug)
        if (!found) {
          setStatus('not-found')
          return
        }
        setPost(found)
        setRelatedPosts(blogs.filter((item) => item.slug !== slug).slice(0, 3))
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  if (status === 'loading') {
    return (
      <div className="container-xxl py-5">
        <div className="container text-center py-5">
          <p className="mb-0">Loading blog…</p>
        </div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="container-xxl py-5">
        <div className="container text-center py-5">
          <p className="mb-0">Could not load this blog right now. Please try again later.</p>
        </div>
      </div>
    )
  }

  if (status === 'not-found' || !post) {
    return (
      <div className="container-xxl py-5">
        <div className="container text-center py-5">
          <h1 className="mb-4">Blog Not Found</h1>
          <p className="mb-4">Sorry, the blog post you are looking for does not exist.</p>
          <Link to="/blogs" className="btn btn-primary px-4 py-2" style={{ borderRadius: '0 30px 30px 0' }}>
            Back to Blogs
          </Link>
        </div>
      </div>
    )
  }

  const paragraphs = post.content.split(/\n\s*\n/).filter((p) => p.trim() !== '')

  const handleShare = async () => {
    const version = post.updatedAt ? new Date(post.updatedAt).getTime() : Date.now()
    const shareUrl = `${window.location.origin}/blogs/${post.slug}?v=${version}`

    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.excerpt, url: shareUrl })
        return
      }
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Share link copied to clipboard!')
    } catch {
      toast.error('Could not copy the share link. Please try again.')
    }
  }

  return (
    <div>
      <div
        className="container-fluid bg-primary py-5 mb-5 hero-header"
        style={{
          background: `linear-gradient(rgba(0, 0, 0, 0.856), rgba(79, 79, 79, 0.801)), url(${process.env.PUBLIC_URL}/assets/img/cover.jpg)`,
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
        }}
      >
        <div className="container py-5">
          <div className="row justify-content-center py-5">
            <div className="col-lg-10 pt-lg-5 mt-lg-5 text-center">
              <h1 className="display-4 text-white animated slideInDown">
                {post.title}
              </h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb justify-content-center">
                  <li className="breadcrumb-item">
                    <a href="/">Home</a>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/blogs" className="text-white">Blogs</Link>
                  </li>
                  <li className="breadcrumb-item text-white active" aria-current="page">
                    {post.title}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </div>

      <div className="container-xxl py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <div className="position-relative overflow-hidden rounded mb-4" style={{ maxHeight: 450 }}>
                <img
                  className="img-fluid w-100"
                  src={post.image}
                  alt={post.title}
                  style={{ objectFit: 'cover', maxHeight: 450, width: '100%' }}
                />
                <div
                  className="position-absolute top-0 start-0 bg-primary text-white px-3 py-1 m-3"
                  style={{ borderRadius: 4, fontSize: '0.8rem' }}
                >
                  {post.category}
                </div>
              </div>

              <small className="text-muted mb-3 d-inline-block">
                <i className="fa fa-calendar-alt me-2" />
                {new Date(post.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </small>

              <h1 className="mb-4">{post.title}</h1>

              {paragraphs.map((paragraph, index) => (
                <p key={index} className="mb-4" style={{ lineHeight: 1.9 }}>
                  {paragraph}
                </p>
              ))}

              <div className="d-flex gap-3 flex-wrap">
                <Link to="/blogs" className="btn btn-primary px-4 py-2 mt-3" style={{ borderRadius: '0 30px 30px 0' }}>
                  Back to Blogs
                </Link>
                <button
                  type="button"
                  onClick={handleShare}
                  className="btn btn-outline-primary px-4 py-2 mt-3"
                  style={{ borderRadius: '0 30px 30px 0' }}
                >
                  <i className="fa fa-share-alt me-2" /> Share
                </button>
              </div>
            </div>
          </div>

          {relatedPosts.length > 0 && (
            <div className="row justify-content-center mt-5 pt-5">
              <div className="col-lg-9">
                <h3 className="mb-4 text-center">More Travel Stories</h3>
                <div className="row g-4">
                  {relatedPosts.map((related) => (
                    <div key={related.slug} className="col-lg-4 col-md-6">
                      <div className="blog-item rounded overflow-hidden h-100 d-flex flex-column shadow-sm">
                        <div className="position-relative overflow-hidden" style={{ height: 180 }}>
                          <img
                            className="img-fluid w-100 h-100"
                            src={related.image}
                            alt={related.title}
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                        <div className="p-3 d-flex flex-column flex-grow-1">
                          <h6 className="mb-3">{related.title}</h6>
                          <Link
                            to={`/blogs/${related.slug}?v=1`}
                            className="btn btn-sm btn-primary px-3 align-self-start mt-auto"
                            style={{ borderRadius: '0 30px 30px 0' }}
                          >
                            Read More
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BlogDetail
